import { format, parseISO, formatDuration } from "date-fns"

// retrieve nested object property via string
let getNestedProperty = (propertyName, object) => {
	let parts = propertyName.split('.')
	let length = parts.length
	let i
	let property = object || this
	for (i = 0; i < length; i++) {
		property = property[parts[i]]
		if (property == null) {
			break
		}
	}
	return property
}

// strip text from any string containing html tags
let displaySafe = (text) => {
	if (text) {
		return text.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/'/g, '&quot;')
			.replace(/'/g, '&#039;')
	} else {
		return ''
	}
}

let formatCurrency = (num) => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

let formatDateTime = (isoDateString) => {
	if (!isoDateString) {
		return ""
	}
	let dateObj = parseISO(isoDateString)
	return format(dateObj, "MMMM d yyyy 'at' h:mma")
}

let formatDateTimeCondensed = (isoDateString) => {
	if (!isoDateString) {
		return ""
	}
	let dateObj = parseISO(isoDateString)
	return format(dateObj, "dd/MM/yyyy 'at' h:mma")
}

let formatDate = (isoDateString) => {
	if (!isoDateString) {
		return ""
	}
	let dateObj = parseISO(isoDateString)
	return format(dateObj, "MMMM d yyyy")
}

let formatDurationFromSec = (seconds) => {
	if (!seconds) {
		return ""
	}
	seconds = Math.round(seconds)
	return formatDuration(
		{ minutes: Math.floor(seconds / 60), seconds: seconds % 60 },
		{ format: ["minutes", "seconds"], delimiter: ', ' }
	)
}

let formatStopwatch = (secondsElapsed) => {
	let displayNegative = secondsElapsed < 0 ? true : false
	if (displayNegative) {
		secondsElapsed = -secondsElapsed
	}
	let minutes = Math.floor(secondsElapsed / 60)
	let seconds = secondsElapsed % 60
	let minutesZeroPadded = String(minutes).padStart(2, "0")
	let secondsZeroPadded = String(seconds).padStart(2, "0")
	let stopwatch = `${minutesZeroPadded}:${secondsZeroPadded}`
	if (displayNegative) {
		stopwatch = "-" + stopwatch
	}
	return stopwatch
}

let trimString = (str, limit) => {
	if (str.length <= limit) {
		return str.trim()
	} else {
		let truncatedStr = str.slice(0, limit).trim()
		if (truncatedStr[truncatedStr.length - 1] === ' ') {
			truncatedStr = truncatedStr.slice(0, -1)
		}
		return truncatedStr + '...'
	}
}

let enterFullScreen = (element) => {
	if (element.requestFullscreen) {
		element.requestFullscreen()
	} else if (element.mozRequestFullScreen) {
		element.mozRequestFullScreen()
	} else if (element.webkitRequestFullscreen) {
		element.webkitRequestFullscreen()
	} else if (element.msRequestFullscreen) {
		element.msRequestFullscreen()
	}
}

let exitFullScreen = () => {
	if (document.exitFullscreen) {
		document.exitFullscreen()
	} else if (document.mozCancelFullScreen) {
		document.mozCancelFullScreen()
	} else if (document.webkitExitFullscreen) {
		document.webkitExitFullscreen()
	}
}

let destroyAllPopovers = () => {
	// Kill all popovers.
	let popovers = document.getElementsByClassName("hotspot-popover")
	try {
		for (let element of popovers) {
			$(element).remove()
		}
	} catch (error) {
		console.log(error)
	}
}

let formatViewCount = (count) => {
	if (count >= 1000) {
		return (Math.round(count / 100) / 10).toFixed(1) + "k"
	}
	return count
}

let getRandomFiveDigitStr = () => {
	let result = ''
	const characters = '0123456789'
	for (let i = 0; i < 5; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length)
		result += characters[randomIndex]
	}
	return result
}

let getRandomIdStr = (length = 6) => {
	let randomString = ""
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
	for (let i = 0; i < length; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length)
		randomString += characters.charAt(randomIndex)
	}
	return randomString
}

let isMobileDevice = () => {
	return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

let filterBlankElements = (elements) => {
	if ([null, undefined].includes(elements)) {
		return []
	}
	return elements.filter(e => {
		let isEmptyText = e.element_type === "text" && ["", "<p></p>"].includes(e.content)
		let isEmptyFile = ["image", "audio", "downloadable", "pdf"].includes(e.element_type) && !e.stored_file
		return !isEmptyText && !isEmptyFile
	})
}

let timeDelay = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Checks if the browser has access to local storage while running within an iframe.
// This is used to make sure that we can set cookies when the application is embedded
// within an iframe on an external site (like an LMS).
let checkIframeLocalStorageAccess = () => {
	return new Promise((resolve) => {
		// Check if the application is currently running inside an iframe.
		const isIframe = window !== window.top
		if (!isIframe) {
			return resolve(true)
		}
		// Check if the browser is Safari - only Safari currently imposes iframe local
		// storage restrictions.
		const userAgent = navigator.userAgent.toLowerCase()
		let isSafari = userAgent.includes('safari') && !userAgent.includes('chrome')
		if (!isSafari) {
			return resolve(true)
		}
		// Check for storage access. If we have storage access, this means we
		// have the ability to use cookies.
		document.hasStorageAccess().then((hasAccess) => {
			if (hasAccess) {
				return resolve(true)
			} else {
				console.log('Iframe does not have storage access')
				return resolve(false)
			}
		}).catch(() => {
			console.log('Failed to check for storage access')
			return resolve(false)
		})
	})
}

export default {
	getNestedProperty,
	displaySafe,
	formatDateTime,
	formatDateTimeCondensed,
	formatDate,
	formatDurationFromSec,
	formatStopwatch,
	formatCurrency,
	trimString,
	enterFullScreen,
	exitFullScreen,
	destroyAllPopovers,
	formatViewCount,
	getRandomFiveDigitStr,
	getRandomIdStr,
	isMobileDevice,
	filterBlankElements,
	timeDelay,
	checkIframeLocalStorageAccess,
}